import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  ImagePlus,
  Upload,
  Trash2,
  Loader2,
  ExternalLink,
  Phone,
  Mail,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Save,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import { COMPANY } from "@/lib/constants";
import { useSiteAssets } from "@/context/SiteAssetsContext";

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB per image (preserves camera quality)

const IMAGE_SLOTS = [
  { key: "hero", label: "Banner principal (Hero)", hint: "Imagen ancha de fondo en la portada" },
  { key: "service_renovation", label: "Servicio · Reformas integrales", hint: "Card grande en la sección Servicios" },
  { key: "service_bathroom", label: "Servicio · Reformas de baños", hint: "Card vertical derecha" },
  { key: "service_solar_ac", label: "Servicio · Placas solares y aire acondicionado", hint: "Card inferior" },
  { key: "service_painting", label: "Servicio · Pintura general", hint: "Card inferior" },
  { key: "service_ceramica", label: "Servicio · Cerámica y alicatados", hint: "Card inferior" },
  { key: "about_team", label: "Sobre nosotros · foto del equipo", hint: "Imagen vertical en la sección sobre nosotros" },
];

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { images, gallery, reload } = useSiteAssets();
  const [tab, setTab] = useState("images");

  const doLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8F9FA] text-neutral-900">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2 text-neutral-700 hover:text-neutral-900 text-xs" data-testid="admin-back-site">
              <ArrowLeft size={14} /> Sitio
            </a>
            <span className="h-5 w-px bg-neutral-200 mx-1" />
            <img src={COMPANY.logo_url} alt="Modolo" className="h-8 w-auto" />
            <div className="hidden sm:block leading-tight">
              <div className="font-display font-extrabold text-sm">Modolo</div>
              <div className="text-[9px] uppercase tracking-[0.24em] text-neutral-500">Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline text-xs text-neutral-500">
              {user?.email}
            </span>
            <button
              data-testid="admin-logout"
              onClick={doLogout}
              className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-700 hover:text-[#E11D2E]"
            >
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex gap-1 border-b border-neutral-200" role="tablist">
          {[
            { id: "images", label: "Imágenes del sitio" },
            { id: "gallery", label: "Galería por áreas" },
            { id: "leads", label: "Pedidos de presupuesto" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 md:px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-[#1F9D40] text-neutral-900"
                  : "border-transparent text-neutral-500 hover:text-neutral-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {tab === "images" && <ImagesTab images={images} onChanged={reload} />}
        {tab === "gallery" && <GalleryTab items={gallery} onChanged={reload} />}
        {tab === "leads" && <LeadsTab />}
      </main>
    </div>
  );
}

// ======================
// IMAGES TAB
// ======================
function ImagesTab({ images, onChanged }) {
  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display font-black text-2xl md:text-3xl tracking-tight">
          Imágenes del sitio
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          Cada bloque es una imagen que aparece en la web. Subí una nueva foto y quedará actualizada al instante.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {IMAGE_SLOTS.map((slot) => (
          <ImageSlot key={slot.key} slot={slot} currentUrl={images[slot.key]} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

function ImageSlot({ slot, currentUrl, onChanged }) {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const pick = () => fileRef.current?.click();

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo imágenes");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Máximo 10 MB por imagen");
      return;
    }
    setLoading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      await api.put(`/images/${slot.key}`, { data_url: dataUrl });
      toast.success("Imagen actualizada");
      await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`admin-image-slot-${slot.key}`}
      className="bg-white border border-neutral-200 overflow-hidden"
    >
      <div className="aspect-[16/9] bg-neutral-100 relative">
        {currentUrl ? (
          <img src={currentUrl} alt={slot.label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">Sin imagen</div>
        )}
        {loading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-sm">
            <Loader2 size={18} className="animate-spin mr-2" /> Subiendo…
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="font-display font-bold text-sm">{slot.label}</div>
        <div className="text-xs text-neutral-500 mt-1">{slot.hint}</div>
        <button
          onClick={pick}
          data-testid={`admin-image-upload-${slot.key}`}
          disabled={loading}
          className="mt-4 inline-flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-full border border-neutral-300 hover:border-[#1F9D40] hover:text-[#1F9D40] disabled:opacity-50"
        >
          <Upload size={12} /> Cambiar foto
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
      </div>
    </div>
  );
}

// ======================
// GALLERY TAB
// ======================
const GALLERY_CATEGORIES = [
  { id: "renovation", label: "Reformas integrales" },
  { id: "bathroom", label: "Baños" },
  { id: "solar_ac", label: "Placas solares & AC" },
  { id: "painting", label: "Pintura" },
  { id: "ceramica", label: "Cerámica" },
];

function categoryLabel(id) {
  return GALLERY_CATEGORIES.find((c) => c.id === id)?.label || id;
}

function GalleryTab({ items, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [localCaptions, setLocalCaptions] = useState({});
  const [uploadCategory, setUploadCategory] = useState("bathroom");
  const [filter, setFilter] = useState("all");

  // Group items by category (sorted by position)
  const allItems = (items || []).map((it) => ({ ...it, category: it.category || "bathroom" }));
  const filteredItems =
    filter === "all"
      ? [...allItems].sort((a, b) => {
          const order = ["renovation", "bathroom", "solar_ac", "painting", "ceramica"];
          const ca = order.indexOf(a.category);
          const cb = order.indexOf(b.category);
          if (ca !== cb) return ca - cb;
          return (a.position || 0) - (b.position || 0);
        })
      : allItems
          .filter((it) => it.category === filter)
          .sort((a, b) => (a.position || 0) - (b.position || 0));

  const countsByCategory = GALLERY_CATEGORIES.reduce((acc, c) => {
    acc[c.id] = allItems.filter((it) => it.category === c.id).length;
    return acc;
  }, {});

  const addFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (!f.type.startsWith("image/")) continue;
        if (f.size > MAX_BYTES) {
          toast.error(`${f.name}: supera 10 MB`);
          continue;
        }
        const dataUrl = await fileToDataUrl(f);
        await api.post("/gallery", { data_url: dataUrl, caption: "", category: uploadCategory });
      }
      toast.success(`Fotos añadidas a ${categoryLabel(uploadCategory)}`);
      setFilter(uploadCategory);
      await onChanged();
    } catch (err) {
      toast.error("No se pudo subir");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Foto eliminada");
      await onChanged();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const move = async (globalIdx, dir) => {
    // Reorder within the current view's category grouping
    const current = filteredItems[globalIdx];
    if (!current) return;
    const sameCat = filteredItems.filter((x) => x.category === current.category);
    const localIdx = sameCat.findIndex((x) => x.id === current.id);
    const newIdx = localIdx + dir;
    if (newIdx < 0 || newIdx >= sameCat.length) return;
    const order = sameCat.map((x) => x.id);
    [order[localIdx], order[newIdx]] = [order[newIdx], order[localIdx]];
    try {
      await api.put("/gallery/reorder", { order, category: current.category });
      await onChanged();
    } catch {
      toast.error("No se pudo reordenar");
    }
  };

  const saveCaption = async (id) => {
    const caption = localCaptions[id] ?? "";
    setSaving(id);
    try {
      await api.patch(`/gallery/${id}`, { caption });
      toast.success("Leyenda guardada");
      await onChanged();
    } catch {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(null);
    }
  };

  const changeCategory = async (id, newCat) => {
    try {
      await api.patch(`/gallery/${id}`, { category: newCat });
      toast.success(`Movido a ${categoryLabel(newCat)}`);
      await onChanged();
    } catch {
      toast.error("No se pudo mover");
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-black text-2xl md:text-3xl tracking-tight">Galería por área</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Sube fotos de cada servicio. Las imágenes se muestran en el sitio en la sección "Proyectos" con pestañas.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={onChanged}
            className="inline-flex items-center gap-2 text-xs text-neutral-600 hover:text-neutral-900"
            data-testid="gallery-refresh"
          >
            <RefreshCw size={13} /> Recargar
          </button>
          <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-full px-1 py-1">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold pl-3 pr-1">
              Subir a
            </label>
            <select
              data-testid="admin-gallery-upload-category"
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
              className="bg-transparent text-xs font-bold outline-none pr-2 py-1 text-neutral-900"
            >
              {GALLERY_CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="gallery-add-btn"
            className="inline-flex items-center gap-2 bg-[#1F9D40] hover:bg-[#177d33] text-white text-xs font-bold px-5 py-3 rounded-full"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
            {uploading ? "Subiendo…" : "Añadir fotos"}
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={addFiles}
            data-testid="gallery-file-input"
          />
        </div>
      </div>

      {/* Category filter */}
      <div className="flex gap-2 flex-wrap mb-6" data-testid="admin-gallery-filter">
        <FilterPill
          label={`Todas (${allItems.length})`}
          active={filter === "all"}
          onClick={() => setFilter("all")}
          id="all"
        />
        {GALLERY_CATEGORIES.map((c) => (
          <FilterPill
            key={c.id}
            id={c.id}
            label={`${c.label} (${countsByCategory[c.id] || 0})`}
            active={filter === c.id}
            onClick={() => setFilter(c.id)}
          />
        ))}
      </div>

      {filteredItems.length === 0 ? (
        <div className="p-10 text-center border border-dashed border-neutral-300 bg-white text-neutral-500 text-sm">
          {filter === "all"
            ? "Aún no hay fotos. Selecciona una categoría arriba, elige 'Subir a' y añade tus primeras fotos."
            : `No hay fotos en ${categoryLabel(filter)}. Selecciona "Subir a → ${categoryLabel(filter)}" y añade fotos.`}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredItems.map((it, idx) => {
            const captionValue = localCaptions[it.id] ?? it.caption ?? "";
            return (
              <div
                key={it.id}
                data-testid={`admin-gallery-item-${it.id}`}
                className="bg-white border border-neutral-200 overflow-hidden"
              >
                <div className="aspect-square bg-neutral-100 relative">
                  <img src={it.data_url} alt="" className="w-full h-full object-cover" />
                  <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-widest bg-neutral-900 text-white px-2 py-0.5 rounded-full">
                    {categoryLabel(it.category)}
                  </span>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between text-[11px] text-neutral-500">
                    <span>Pos. #{(it.position || 0) + 1} en {categoryLabel(it.category)}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => move(idx, -1)}
                        className="w-7 h-7 rounded-full border border-neutral-200 flex items-center justify-center hover:border-neutral-400"
                        aria-label="subir"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        onClick={() => move(idx, 1)}
                        className="w-7 h-7 rounded-full border border-neutral-200 flex items-center justify-center hover:border-neutral-400"
                        aria-label="bajar"
                      >
                        <ChevronDown size={14} />
                      </button>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Leyenda (opcional) · ej: Torrevieja"
                    value={captionValue}
                    onChange={(e) =>
                      setLocalCaptions((prev) => ({ ...prev, [it.id]: e.target.value }))
                    }
                    className="w-full border-b border-neutral-200 focus:border-[#1F9D40] outline-none py-2 text-sm"
                  />
                  <div className="flex items-center justify-between gap-3">
                    <button
                      onClick={() => saveCaption(it.id)}
                      disabled={saving === it.id || localCaptions[it.id] === undefined}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1F9D40] hover:text-[#177d33] disabled:opacity-40"
                    >
                      {saving === it.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                      Guardar
                    </button>
                    <select
                      value={it.category}
                      data-testid={`admin-gallery-move-${it.id}`}
                      onChange={(e) => changeCategory(it.id, e.target.value)}
                      className="text-[11px] bg-transparent border border-neutral-200 rounded-full px-3 py-1 font-semibold text-neutral-600 outline-none hover:border-neutral-400"
                    >
                      {GALLERY_CATEGORIES.map((c) => (
                        <option key={c.id} value={c.id}>
                          Mover a {c.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => remove(it.id)}
                      data-testid={`admin-gallery-delete-${it.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 hover:text-[#E11D2E]"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterPill({ id, label, active, onClick }) {
  return (
    <button
      data-testid={`admin-gallery-filter-${id}`}
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border transition-all ${
        active
          ? "bg-neutral-900 text-white border-neutral-900"
          : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"
      }`}
    >
      {label}
    </button>
  );
}

// ======================
// LEADS TAB
// ======================
function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openLead, setOpenLead] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data || []);
    } catch (err) {
      toast.error("No se pudieron cargar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este pedido?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Eliminado");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const serviceLabel = (s) =>
    ({
      renovation: "Reforma integral",
      bathroom: "Reforma de baño",
      solar: "Placas solares",
      ac: "Aire acondicionado",
      painting: "Pintura",
      other: "Otro",
    }[s] || s);

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-2xl md:text-3xl tracking-tight">Pedidos de presupuesto</h1>
          <p className="text-sm text-neutral-500 mt-1">
            {leads.length} pedido{leads.length === 1 ? "" : "s"} en total
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 text-xs text-neutral-600 hover:text-neutral-900"
          data-testid="leads-refresh"
        >
          <RefreshCw size={13} /> Recargar
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-neutral-500 text-sm"><Loader2 className="inline animate-spin mr-2" size={14} /> Cargando…</div>
      ) : leads.length === 0 ? (
        <div className="p-10 text-center border border-dashed border-neutral-300 bg-white text-neutral-500 text-sm">
          Aún no has recibido ningún pedido.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-leads-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
                <th className="p-4">Fecha</th>
                <th className="p-4">Nombre</th>
                <th className="p-4">Teléfono</th>
                <th className="p-4 hidden md:table-cell">Email</th>
                <th className="p-4">Servicio</th>
                <th className="p-4 hidden lg:table-cell">Fotos</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => {
                const date = l.created_at ? new Date(l.created_at) : null;
                return (
                  <tr key={l.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`admin-lead-row-${l.id}`}>
                    <td className="p-4 text-neutral-500 whitespace-nowrap">
                      {date ? date.toLocaleDateString("es-ES") : "-"}
                      <div className="text-[10px] text-neutral-400">
                        {date ? date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : ""}
                      </div>
                    </td>
                    <td className="p-4 font-semibold">{l.name}</td>
                    <td className="p-4">
                      <a href={`tel:${l.phone}`} className="text-neutral-700 hover:text-[#1F9D40] inline-flex items-center gap-1.5">
                        <Phone size={12} /> {l.phone}
                      </a>
                    </td>
                    <td className="p-4 hidden md:table-cell text-neutral-600 break-all">
                      {l.email ? (
                        <a href={`mailto:${l.email}`} className="hover:text-[#1F9D40] inline-flex items-center gap-1.5">
                          <Mail size={12} /> {l.email}
                        </a>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="p-4">{serviceLabel(l.service)}</td>
                    <td className="p-4 hidden lg:table-cell">
                      {l.photos?.length ? (
                        <span className="text-[11px] bg-[#1F9D40]/10 text-[#1F9D40] font-semibold px-2 py-0.5 rounded-full">
                          {l.photos.length} foto{l.photos.length === 1 ? "" : "s"}
                        </span>
                      ) : (
                        <span className="text-neutral-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => setOpenLead(l)}
                        data-testid={`admin-lead-view-${l.id}`}
                        className="inline-flex items-center gap-1 text-xs font-bold text-[#1F9D40] hover:text-[#177d33] mr-3"
                      >
                        Ver <ExternalLink size={11} />
                      </button>
                      <button
                        onClick={() => remove(l.id)}
                        className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#E11D2E]"
                      >
                        <Trash2 size={11} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openLead && <LeadModal lead={openLead} onClose={() => setOpenLead(null)} />}
    </div>
  );
}

function LeadModal({ lead, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      data-testid="admin-lead-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8 border-b border-neutral-200">
          <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">Pedido</div>
          <h3 className="font-display font-black text-xl md:text-2xl mt-1">{lead.name}</h3>
          <div className="text-sm text-neutral-500 mt-1">
            {lead.created_at ? new Date(lead.created_at).toLocaleString("es-ES") : ""}
          </div>
        </div>
        <div className="p-6 md:p-8 space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Teléfono</div>
              <a href={`tel:${lead.phone}`} className="text-neutral-900 hover:text-[#1F9D40]">{lead.phone}</a>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Email</div>
              <div className="break-all">
                {lead.email ? (
                  <a href={`mailto:${lead.email}`} className="text-neutral-900 hover:text-[#1F9D40]">{lead.email}</a>
                ) : <span className="text-neutral-400">—</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Servicio</div>
              <div>{lead.service}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Idioma</div>
              <div>{lead.locale?.toUpperCase() || "ES"}</div>
            </div>
          </div>
          {lead.message ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">Mensaje</div>
              <p className="text-sm text-neutral-700 whitespace-pre-wrap">{lead.message}</p>
            </div>
          ) : null}
          {lead.photos?.length ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2">Fotos adjuntas</div>
              <div className="grid grid-cols-3 gap-2">
                {lead.photos.map((p, i) => (
                  <a key={i} href={p} target="_blank" rel="noopener noreferrer">
                    <img src={p} alt="" className="w-full aspect-square object-cover hover:opacity-80" />
                  </a>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <div className="p-6 md:p-8 border-t border-neutral-200 flex items-center justify-end gap-3">
          <a
            href={`https://wa.me/${lead.phone.replace(/\D/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#25D366] hover:text-neutral-900"
          >
            Contactar por WhatsApp
          </a>
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-full bg-neutral-900 text-white text-sm font-bold hover:bg-neutral-800"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
